// Email/password sign-in runs entirely client-side via SRP (see cognitoSrp.ts) -- no Hosted UI
// page is ever shown for it. Google is the one exception: Cognito User Pool federation has no
// direct-SRP equivalent, so it still bounces through Cognito's /oauth2/authorize endpoint, but
// with identity_provider=Google preset so Cognito forwards straight to Google's own account
// screen without ever rendering its generic Hosted UI login page in between.

import { config } from '../config';

export function buildGoogleAuthorizeUrl(): string {
  const params = new URLSearchParams({
    response_type: 'code',
    client_id: config.cognitoClientId,
    redirect_uri: config.cognitoRedirectUri,
    scope: config.cognitoScope,
    identity_provider: 'Google',
  });
  return `${config.cognitoDomain}/oauth2/authorize?${params.toString()}`;
}

export function buildLogoutUrl(): string {
  const params = new URLSearchParams({
    client_id: config.cognitoClientId,
    logout_uri: config.cognitoLogoutUri,
  });
  return `${config.cognitoDomain}/logout?${params.toString()}`;
}

interface TokenResponse {
  id_token: string;
  access_token: string;
  refresh_token?: string;
  token_type: string;
  expires_in: number;
}

/**
 * Exchange an authorization code for tokens via Cognito's token endpoint.
 * Returns the ID token (the Bearer token the API contract requires) plus a
 * refresh token when Cognito issues one -- Google/Hosted-UI logins have no
 * amazon-cognito-identity-js session to fall back on for renewal (see
 * refreshIdToken below), so this is the only copy of it the app gets.
 */
export async function exchangeCodeForToken(code: string): Promise<{ idToken: string; refreshToken?: string }> {
  const body = new URLSearchParams({
    grant_type: 'authorization_code',
    client_id: config.cognitoClientId,
    code,
    redirect_uri: config.cognitoRedirectUri,
  });

  const res = await fetch(`${config.cognitoDomain}/oauth2/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: body.toString(),
  });

  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`Token exchange failed (${res.status}): ${text}`);
  }

  const data = (await res.json()) as TokenResponse;
  return { idToken: data.id_token, refreshToken: data.refresh_token };
}

/**
 * Renews an ID token using a previously-stored refresh token (the
 * Google/Hosted-UI login path -- see auth/token.ts's getRefreshToken()).
 * Cognito doesn't reissue a new refresh token on this grant, so the caller
 * keeps using the one it already has.
 */
export async function refreshIdToken(refreshToken: string): Promise<string> {
  const body = new URLSearchParams({
    grant_type: 'refresh_token',
    client_id: config.cognitoClientId,
    refresh_token: refreshToken,
  });

  const res = await fetch(`${config.cognitoDomain}/oauth2/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: body.toString(),
  });

  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`Token refresh failed (${res.status}): ${text}`);
  }

  const data = (await res.json()) as TokenResponse;
  return data.id_token;
}
